import type { EditorDraft } from "./editor-types";
import type { ProfileData, SectionData } from "@profolio/templates";

export interface FieldDiff {
  field: string;
  baseValue: unknown;
  localValue: unknown;
  serverValue: unknown;
}

export interface SectionDiff {
  sectionId: string;
  sectionType: string;
  kind:
    | "added_locally"
    | "added_on_server"
    | "deleted_locally"
    | "deleted_on_server"
    | "modified_locally"
    | "modified_on_server"
    | "collision";
  localSection?: SectionData;
  serverSection?: SectionData;
  baseSection?: SectionData;
}

export interface ThreeWayRebaseResult {
  hasCollisions: boolean;
  collidingProfileFields: FieldDiff[];
  collidingSections: SectionDiff[];
  rebasedDraft: EditorDraft;
  allDiffs: {
    profileChanges: FieldDiff[];
    sectionChanges: SectionDiff[];
  };
}

function stableStringify(value: unknown): string {
  if (value === undefined) return "__undefined__";
  if (value === null) return "null";
  return JSON.stringify(value);
}

/**
 * Performs a deterministic 3-way merge between baseDraft, localDraft, and serverDraft.
 * Classifies all changes into local-only, server-only, and true collisions.
 */
export function performThreeWayRebase(
  baseDraft: EditorDraft,
  localDraft: EditorDraft,
  serverDraft: EditorDraft
): ThreeWayRebaseResult {
  const collidingProfileFields: FieldDiff[] = [];
  const profileChanges: FieldDiff[] = [];
  const rebasedProfile: ProfileData = { ...serverDraft.profile };

  // ── 1. Three-Way Merge of Profile Fields ───────────────────────────────────
  const allProfileKeys = new Set<keyof ProfileData>([
    ...(Object.keys(baseDraft.profile) as Array<keyof ProfileData>),
    ...(Object.keys(localDraft.profile) as Array<keyof ProfileData>),
    ...(Object.keys(serverDraft.profile) as Array<keyof ProfileData>),
  ]);

  for (const key of allProfileKeys) {
    const baseVal = baseDraft.profile[key];
    const localVal = localDraft.profile[key];
    const serverVal = serverDraft.profile[key];

    const baseStr = stableStringify(baseVal);
    const localStr = stableStringify(localVal);
    const serverStr = stableStringify(serverVal);

    // Case 1: Unchanged
    if (localStr === baseStr && serverStr === baseStr) {
      // Retain server value
      (rebasedProfile as unknown as Record<string, unknown>)[key] = serverVal;
      continue;
    }

    // Case 2: Changed only locally -> adopt local
    if (localStr !== baseStr && serverStr === baseStr) {
      (rebasedProfile as unknown as Record<string, unknown>)[key] = localVal;
      profileChanges.push({ field: String(key), baseValue: baseVal, localValue: localVal, serverValue: serverVal });
      continue;
    }

    // Case 3: Changed only on server -> adopt server
    if (localStr === baseStr && serverStr !== baseStr) {
      (rebasedProfile as unknown as Record<string, unknown>)[key] = serverVal;
      profileChanges.push({ field: String(key), baseValue: baseVal, localValue: localVal, serverValue: serverVal });
      continue;
    }

    // Case 4: Changed on both local and server
    if (localStr !== baseStr && serverStr !== baseStr) {
      if (localStr === serverStr) {
        // Both changed to identical value -> clean merge
        (rebasedProfile as unknown as Record<string, unknown>)[key] = localVal;
      } else {
        // True Collision!
        collidingProfileFields.push({
          field: String(key),
          baseValue: baseVal,
          localValue: localVal,
          serverValue: serverVal,
        });
        profileChanges.push({ field: String(key), baseValue: baseVal, localValue: localVal, serverValue: serverVal });
        // Tentatively keep local value in draft
        (rebasedProfile as unknown as Record<string, unknown>)[key] = localVal;
      }
    }
  }

  // ── 2. Three-Way Merge of Sections ─────────────────────────────────────────
  const collidingSections: SectionDiff[] = [];
  const sectionChanges: SectionDiff[] = [];

  const baseSecMap = new Map<string, SectionData>(baseDraft.sections.map((s) => [s.id, s]));
  const localSecMap = new Map<string, SectionData>(localDraft.sections.map((s) => [s.id, s]));
  const serverSecMap = new Map<string, SectionData>(serverDraft.sections.map((s) => [s.id, s]));

  const allSectionIds = new Set<string>([
    ...baseSecMap.keys(),
    ...localSecMap.keys(),
    ...serverSecMap.keys(),
  ]);

  const rebasedSectionMap = new Map<string, SectionData>();

  for (const secId of allSectionIds) {
    const baseSec = baseSecMap.get(secId);
    const localSec = localSecMap.get(secId);
    const serverSec = serverSecMap.get(secId);

    const inBase = Boolean(baseSec);
    const inLocal = Boolean(localSec);
    const inServer = Boolean(serverSec);

    // Case A: Exists in all three
    if (inBase && inLocal && inServer && baseSec && localSec && serverSec) {
      const baseContentStr = stableStringify({ content: baseSec.content, visible: baseSec.visible, pageId: baseSec.pageId });
      const localContentStr = stableStringify({ content: localSec.content, visible: localSec.visible, pageId: localSec.pageId });
      const serverContentStr = stableStringify({ content: serverSec.content, visible: serverSec.visible, pageId: serverSec.pageId });

      if (localContentStr === baseContentStr && serverContentStr === baseContentStr) {
        // Unchanged
        rebasedSectionMap.set(secId, serverSec);
      } else if (localContentStr !== baseContentStr && serverContentStr === baseContentStr) {
        // Local modification only -> adopt local
        rebasedSectionMap.set(secId, localSec);
        sectionChanges.push({
          sectionId: secId,
          sectionType: localSec.type,
          kind: "modified_locally",
          localSection: localSec,
          serverSection: serverSec,
          baseSection: baseSec,
        });
      } else if (localContentStr === baseContentStr && serverContentStr !== baseContentStr) {
        // Server modification only -> adopt server
        rebasedSectionMap.set(secId, serverSec);
        sectionChanges.push({
          sectionId: secId,
          sectionType: serverSec.type,
          kind: "modified_on_server",
          localSection: localSec,
          serverSection: serverSec,
          baseSection: baseSec,
        });
      } else {
        // Modified in both
        if (localContentStr === serverContentStr) {
          rebasedSectionMap.set(secId, localSec);
        } else {
          // True Collision!
          collidingSections.push({
            sectionId: secId,
            sectionType: localSec.type,
            kind: "collision",
            localSection: localSec,
            serverSection: serverSec,
            baseSection: baseSec,
          });
          sectionChanges.push({
            sectionId: secId,
            sectionType: localSec.type,
            kind: "collision",
            localSection: localSec,
            serverSection: serverSec,
            baseSection: baseSec,
          });
          // Tentatively keep local
          rebasedSectionMap.set(secId, localSec);
        }
      }
      continue;
    }

    // Case B: Added locally (in Local, not in Base, not in Server)
    if (!inBase && inLocal && !inServer && localSec) {
      rebasedSectionMap.set(secId, localSec);
      sectionChanges.push({
        sectionId: secId,
        sectionType: localSec.type,
        kind: "added_locally",
        localSection: localSec,
      });
      continue;
    }

    // Case C: Added on server (in Server, not in Base, not in Local)
    if (!inBase && !inLocal && inServer && serverSec) {
      rebasedSectionMap.set(secId, serverSec);
      sectionChanges.push({
        sectionId: secId,
        sectionType: serverSec.type,
        kind: "added_on_server",
        serverSection: serverSec,
      });
      continue;
    }

    // Case D: Deleted locally (in Base, not in Local, in Server)
    if (inBase && !inLocal && inServer && baseSec && serverSec) {
      const baseContentStr = stableStringify({ content: baseSec.content, visible: baseSec.visible, pageId: baseSec.pageId });
      const serverContentStr = stableStringify({ content: serverSec.content, visible: serverSec.visible, pageId: serverSec.pageId });

      if (serverContentStr === baseContentStr) {
        // Server untouched -> delete safely
        sectionChanges.push({
          sectionId: secId,
          sectionType: baseSec.type,
          kind: "deleted_locally",
          baseSection: baseSec,
          serverSection: serverSec,
        });
      } else {
        // Server was modified while local deleted -> collision!
        collidingSections.push({
          sectionId: secId,
          sectionType: baseSec.type,
          kind: "collision",
          baseSection: baseSec,
          serverSection: serverSec,
        });
        // Tentatively preserve server section
        rebasedSectionMap.set(secId, serverSec);
      }
      continue;
    }

    // Case E: Deleted on server (in Base, in Local, not in Server)
    if (inBase && inLocal && !inServer && baseSec && localSec) {
      const baseContentStr = stableStringify({ content: baseSec.content, visible: baseSec.visible, pageId: baseSec.pageId });
      const localContentStr = stableStringify({ content: localSec.content, visible: localSec.visible, pageId: localSec.pageId });

      if (localContentStr === baseContentStr) {
        // Local was untouched -> accept deletion
        sectionChanges.push({
          sectionId: secId,
          sectionType: baseSec.type,
          kind: "deleted_on_server",
          baseSection: baseSec,
          localSection: localSec,
        });
      } else {
        // Local modified while server deleted -> collision!
        collidingSections.push({
          sectionId: secId,
          sectionType: localSec.type,
          kind: "collision",
          baseSection: baseSec,
          localSection: localSec,
        });
        // Tentatively preserve local section
        rebasedSectionMap.set(secId, localSec);
      }
      continue;
    }
  }

  // Preserve display ordering based on local preference then server additions
  const finalSections = Array.from(rebasedSectionMap.values()).map((s, idx) => ({
    ...s,
    displayOrder: idx,
  }));

  const hasCollisions = collidingProfileFields.length > 0 || collidingSections.length > 0;

  const rebasedDraft: EditorDraft = {
    profile: rebasedProfile,
    sections: finalSections,
    designPreset: localDraft.designPreset || serverDraft.designPreset,
    draftVersion: serverDraft.draftVersion,
  };

  return {
    hasCollisions,
    collidingProfileFields,
    collidingSections,
    rebasedDraft,
    allDiffs: {
      profileChanges,
      sectionChanges,
    },
  };
}
